import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Crown, Ticket, ShieldCheck, CalendarClock, Send,
  Trophy, CheckCircle2, Plus, Eye, ChevronRight,
  CircleDollarSign, UsersRound, WalletCards, Loader2,
} from "lucide-react";
import UserSideNav from "../../components/user/UserSideNav";
import UserHeader from "../../components/user/UserHeader";
import SummaryCard from "../../components/user/SummaryCard";
import NumberBalls from "../../components/shared/NumberBalls";
import { useAuth } from "../../context/AuthContext";
import { usersApi, poolsApi, ticketsApi, winnersApi, ticketRequestsApi } from "../../services/api";

function fmt(amount) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
}

export default function UserOverview() {
  const { user } = useAuth();
  const firstName = user?.name?.split(" ")[0] || "there";

  const [summary, setSummary] = useState(null);
  const [pools, setPools] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [winnings, setWinnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      const [sumResult, poolResult, ticketResult, winResult, requestResult] = await Promise.allSettled([
        usersApi.getMySummary(),
        poolsApi.getMyPools(),
        ticketsApi.getMyTickets(),
        winnersApi.getMyWinnings(),
        ticketRequestsApi.getMine(),
      ]);

      if (sumResult.status === "fulfilled") setSummary(sumResult.value.data.data);
      else setError(sumResult.reason?.response?.data?.message || "Failed to load dashboard summary.");

      setPools(poolResult.status === "fulfilled" ? poolResult.value.data.data || [] : []);
      setTickets(ticketResult.status === "fulfilled" ? (ticketResult.value.data.data || []).slice(0, 2) : []);
      setWinnings(winResult.status === "fulfilled" ? (winResult.value.data.data || []).slice(0, 5) : []);
      setRequests(requestResult.status === "fulfilled" ? requestResult.value.data.data || [] : []);
      setLoading(false);
    }
    load();
  }, []);

  const activePool = pools.find((m) => m.pool?.status === "filling" || m.pool?.status === "locked");
  const pool = activePool?.pool;

  const summaryCards = [
    { icon: Ticket, label: "Ticket Requests", value: String(requests.length || "0"), detail: "purchase orders" },
    { icon: CircleDollarSign, label: "Total Payments", value: fmt(summary?.totalPaid), detail: "all time" },
    { icon: ShieldCheck, label: "Assigned Tickets", value: String(tickets.length || "0"), detail: "admin verified" },
    { icon: UsersRound, label: "Active Pools", value: String(pools.length || "0"), detail: "with ticket activity" },
  ];

  if (loading) return (
    <div className="user-shell">
      <UserSideNav />
      <div className="user-app">
        <UserHeader />
        <div className="page-loading"><Loader2 size={32} className="spin" /><span>Loading dashboard…</span></div>
      </div>
    </div>
  );

  return (
    <div className="user-shell">
      <UserSideNav />
      <div className="user-app">
        <UserHeader />
        <main className="user-main">
          {error && <div className="auth-error" style={{ marginBottom: 0 }}>{error}</div>}

          <div className="welcome-block">
            <div>
              <div className="page-kicker">Dashboard Overview</div>
              <h1>Welcome back, {firstName}</h1>
              <p>Request Powerball tickets, track admin assignment, and review pool outcomes from one operational dashboard.</p>
            </div>
            <Link to="/dashboard/pool" className="hero-action">
              Request Tickets <ChevronRight size={18} />
            </Link>
          </div>

          <section className="summary-grid" aria-label="User summary">
            {summaryCards.map((item) => <SummaryCard key={item.label} {...item} />)}
          </section>

          <section className="user-dashboard-grid">
            <div className="user-left-column">
              <section className="section-block">
                <div className="section-heading">
                  <h2><Crown size={22} /> Active Pool</h2>
                  <Link to="/dashboard/pool">All Pools</Link>
                </div>
                {pool ? (
                  <article className="pool-hero">
                    <div className="pool-copy">
                      <div className="meta-row">
                        <span className="status-pill live">Active Now</span>
                        <span>ID: {pool.poolId}</span>
                      </div>
                      <h3>{pool.name}</h3>
                      <p>Your ticket requests and admin-assigned entries are organized against this draw.</p>
                      <div className="pool-metrics">
                        <div>
                          <span className="metric-label">Participation</span>
                          <strong className="metric-value gold">
                            {pool.memberCount} <small>/ {pool.size} Slots Filled</small>
                          </strong>
                          <span className="progress">
                            <i style={{ width: `${Math.min((pool.memberCount / pool.size) * 100, 100)}%` }} />
                          </span>
                        </div>
                        <div>
                          <span className="metric-label">Draw Date</span>
                          <strong style={{ fontSize: 22, display: "block", marginTop: 4 }}>{fmtDate(pool.drawDate)}</strong>
                        </div>
                      </div>
                    </div>
                    <aside className="jackpot-card">
                      <span>Est. Jackpot</span>
                      <strong>{pool.jackpotEstimate ? fmt(pool.jackpotEstimate) : "TBA"}</strong>
                      <em>Powerball Draw</em>
                      <Link to="/dashboard/pool" style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", background: "var(--gold)", color: "#402d00", fontWeight: 800, padding: "15px 20px", borderRadius: "0 0 12px 12px", margin: "28px -1px -1px", textDecoration: "none", width: "111%" }}>
                        <Plus size={16} /> Request More
                      </Link>
                    </aside>
                  </article>
                ) : (
                  <div className="empty-state">
                    <UsersRound size={36} />
                    <p>You do not have active pool activity yet.</p>
                    <Link to="/dashboard/pool" className="hero-action" style={{ fontSize: 14, minHeight: 40 }}>Request Tickets</Link>
                  </div>
                )}
              </section>

              <section className="section-block">
                <div className="section-heading">
                  <h2><Ticket size={20} /> Recent Assigned Tickets</h2>
                  <Link to="/dashboard/tickets">View All</Link>
                </div>
                {tickets.length > 0 ? (
                  <div className="ticket-grid">
                    {tickets.map((t) => (
                      <article className="ticket-card" key={t._id}>
                        <div className="ticket-top">
                          <div><span>Draw Date</span><strong>{fmtDate(t.drawDate)}</strong></div>
                          <em>{t.status}</em>
                        </div>
                        <NumberBalls numbers={t.whiteBalls} power={t.powerball} />
                        {t.imageUrl && (
                          <a href={`http://localhost:5000${t.imageUrl}`} target="_blank" rel="noreferrer">
                            <button><Eye size={14} /> View Physical Ticket</button>
                          </a>
                        )}
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state"><Ticket size={28} /><p>No assigned tickets yet. Paid requests will appear after admin purchase.</p></div>
                )}
              </section>
            </div>

            <aside className="user-right-column">
              <section className="section-block">
                <div className="section-heading"><h2><ShieldCheck size={20} /> Subscription</h2></div>
                <article className="subscription-card">
                  <span className={`status-pill ${user?.subscriptionStatus === "active" ? "live" : ""}`}>
                    {user?.subscriptionStatus === "active" ? "Active" : "Inactive"}
                  </span>
                  <h3>Ticket Request Support</h3>
                  <p>Keep payment and request settings current</p>
                  <ul>
                    <li><CheckCircle2 size={15} /> Paid request history</li>
                    <li><CheckCircle2 size={15} /> Manual ticket assignment</li>
                    <li><CheckCircle2 size={15} /> Outcome notifications</li>
                  </ul>
                  <Link to="/dashboard/subscription" className="auth-submit" style={{ display: "flex", justifyContent: "center", textDecoration: "none", minHeight: 42, alignItems: "center", fontSize: 14, color:'#000' }}>
                    Manage Subscription
                  </Link>
                </article>
              </section>

              <section className="section-block">
                <div className="section-heading"><h2><CalendarClock size={20} /> Next Draw</h2></div>
                <div className="draw-status-card">
                  {pool ? (
                    <>
                      <div><span>Powerball Draw</span><strong>{fmtDate(pool.drawDate)}</strong></div>
                    <p>Pool {pool.poolId} is active. Ticket proof appears after admin buys and assigns your tickets.</p>
                    </>
                  ) : (
                    <p style={{ color: "var(--muted)", fontSize: 13 }}>Join a pool to see your next draw date.</p>
                  )}
                </div>
              </section>

              <section className="section-block">
                <div className="section-heading"><h2><Send size={20} /> Quick Links</h2></div>
                <div className="referral-card">
                  <Link to="/dashboard/payments" style={{ color: "var(--gold)", fontWeight: 800, fontSize: 14 }}>View Payment History →</Link>
                  <Link to="/dashboard/results" style={{ color: "var(--gold)", fontWeight: 800, fontSize: 14 }}>Check Draw Results →</Link>
                  <Link to="/dashboard/profile" style={{ color: "var(--muted)", fontSize: 13 }}>Update Profile</Link>
                </div>
              </section>
            </aside>
          </section>

          {winnings.length > 0 && (
            <section className="section-block">
              <div className="section-heading">
                <h2><Trophy size={20} /> Winnings History</h2>
                <Link to="/dashboard/results">View All</Link>
              </div>
              <div className="data-table user-table">
                <div className="table-head"><span>Date</span><span>Pool</span><span>Ticket</span><span>Share</span><span>Status</span></div>
                {winnings.map((w) => (
                  <div className="table-row" key={w._id}>
                    <span>{fmtDate(w.createdAt)}</span>
                    <strong>{w.pool?.poolId || "—"}</strong>
                    <code>{w.ticket?.ticketNumber || "—"}</code>
                    <b>{fmt(w.individualShare)}</b>
                    <em>{w.payoutStatus}</em>
                  </div>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
